import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { BuildingIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { Button } from "simplydo/react/ui/button";
import { CardContent } from "simplydo/react/ui/card";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

import IdeaBusinessProfileModal from "components/lib/IdeaBusinessProfileModal/IdeaBusinessProfileModal";
import type { IdeaBusinessProfileRef } from "components/lib/IdeaBusinessProfileModal/types";

import {
  HomeRow,
  HomeRowActions,
  HomeRowContainer,
  HomeRowContent,
  HomeRowIcon,
} from "../HomeUI";
import HomeBlock from "./HomeBlock";

const BusinessProfilesBlock = () => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [viewingProfile, setViewingProfile] =
    useState<IdeaBusinessProfileRef | null>(null);
  const dashboardQuery = useQuery(initUseQuery(api.users.dashboard, []));
  const enabled = user?.ownerOrganisation?.enabledFeatures?.includes(
    "ideaBusinessProfiles",
  );
  const profiles = dashboardQuery.data?.businessProfiles ?? [];

  if (!enabled || !profiles.length) return null;

  const countPerColumn = 3;
  const columns = profiles.length > countPerColumn ? 2 : 1;

  return (
    <>
      {viewingProfile ? (
        <IdeaBusinessProfileModal
          businessProfile={viewingProfile}
          onClose={() => setViewingProfile(null)}
        />
      ) : null}
      <HomeBlock $width={columns}>
        <CardContent>
          <H3>
            {t("home.businessProfiles.title")}
            <Subheader>{t("home.businessProfiles.info")}</Subheader>
          </H3>
          <HomeRowContainer $columns={columns}>
            {profiles.map((profile) => (
              <HomeRow key={profile._id}>
                <HomeRowIcon $contain>
                  {profile.imageUrl ? (
                    <Image src={profile.imageUrl} />
                  ) : (
                    <Icon icon={BuildingIcon} size="large" />
                  )}
                </HomeRowIcon>
                <HomeRowContent>
                  <H4>
                    {profile.name}
                    {profile.websiteUrl ? (
                      <Subheader>{profile.websiteUrl}</Subheader>
                    ) : null}
                  </H4>
                </HomeRowContent>
                <HomeRowActions>
                  <Button
                    color="primary"
                    variant="outline"
                    size="small"
                    onClick={() => setViewingProfile(profile)}
                  >
                    {t("home.businessProfiles.view")}
                  </Button>
                </HomeRowActions>
              </HomeRow>
            ))}
          </HomeRowContainer>
        </CardContent>
      </HomeBlock>
    </>
  );
};

export default BusinessProfilesBlock;
